import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { WebSocketProvider } from './context/WebSocketContext';
import { api } from './services/api';

// Sentry Integration
import * as Sentry from "@sentry/react";

// Import pages
import HomePage from './pages/static/HomePage'; 
import PricingPage from './pages/static/PricingPage';
import PrivacyPolicy from './pages/static/PrivacyPolicy';
import TermsOfService from './pages/static/TermsOfService';
import DataProcessingAgreement from './pages/static/DataProcessingAgreement';


import { NotificationProvider } from './Notifications';
import { ErrorProvider } from './context/ErrorContext';
import useError from './context/useError';

import Signup from './pages/auth/Signup';
import Login from './pages/auth/login';
import { useAuth } from './components/auth/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Callback from './Callback';
import './index.css';
import './styling/auth.css';
import CreditsModal from './payment/CreditsModal';
import Sidebar from './components/layout/Sidebar';
import { useResponsive } from './components/vm/useResponsive';

import BillingPage from './pages/BillingPage';
import Dashboard from './dashboard/dashboard';
import DeployVM from './pages/DeployVM';
import MyAccount from './pages/auth/MyAccount';
import VolumesPage from './components/Volumes/VolumesPage';
import SSHKeysPage from './components/ssh/SshKeysPage';
import FirewallPage from './components/Firewall/Firewallpage';
import SnapshotPage from './components/Snapshot/SnapshotPage';
import ImagePage from './components/Image/ImagePage';

// Set axios defaults
axios.defaults.withCredentials = true;
const API_BASE_URL = 'https://barrack.ai';

// Initialize Sentry
Sentry.init({
  dsn: "https://3b8a00b61f6f6f949f3ccb4198f13bac@o4509405856727040.ingest.us.sentry.io/4509405857972225",
  environment: process.env.NODE_ENV || 'development',
  sendDefaultPii: true,
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 0.01,
  replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 0,
  replaysOnErrorSampleRate: 1.0,
});

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'app' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'app' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'app',
    level: 'info',
    data: context
  });
};

// Admin route protection HOC
const AdminRoute = ({ children }) => {
  return children;
};

// ✅ NEW: AuthGuard component that waits for initialization
const AuthGuard = ({ children, fallbackPath = '/login' }) => {
  const { isAuthenticated, isInitialized, isLoading } = useAuth();
  const location = window.location;
  
  // Show loading while auth is initializing
  if (!isInitialized || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-white text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  
  // After initialization, check authentication
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location.pathname }} replace />;
  }
  
  return children;
};

const App = () => {
  const { isAuthenticated, user, isLoading: authLoading, isInitialized, handleAuthError } = useAuth();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResponsive();
  
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [creditBalance, setCreditBalance] = useState({ inr: 0, usd: 0, eur: 0 });
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [hoveredElement, setHoveredElement] = useState(null);

  // Update Sentry user context when user data changes
  useEffect(() => {
    if (isAuthenticated && user) {
      Sentry.setUser({
        email: user.email,
        id: user.barrackId,
        authenticated: true
      });
      
      logInfo("Sentry user context updated", {
        email: user.email,
        barrackId: user.barrackId
      });
    } else {
      Sentry.setUser(null);
      logInfo("Sentry user context cleared");
    }
  }, [isAuthenticated, user]);

const fetchCreditBalance = async () => {
  try {
    const response = await api.getCreditBalance();
    
    if (response.success) {
      setCreditBalance(response.balance);
      logInfo('Credit balance fetched successfully', {
        balance: response.balance,
        userEmail: user?.email
      });
    } else {
      logError('Failed to fetch credit balance', {
        userEmail: user?.email,
        error: response.error
      });
    }
  } catch (err) {
    // Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      handleAuthError();
      return;
    }
    
    logError('Failed to fetch credit balance', {
      userEmail: user?.email,
      authenticated: isAuthenticated,
      errorCode: err.code,
      errorMessage: err.error
    }, err);
  }
};
  const handleProtectedNavigation = (path) => {
    if (isAuthenticated) {
      navigate(path);
    } else {
      localStorage.setItem('auth_redirect_path', path);
      logInfo('Redirecting unauthenticated user to login', {
        intendedDestination: path,
        currentPath: window.location.pathname
      });
      navigate('/login');
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleAuthAction = (action) => {
    logInfo('Auth action triggered', { 
      action,
      currentPath: window.location.pathname,
      authenticated: isAuthenticated 
    });
    
    if (action === 'login') {
      navigate('/login');
    } else if (action === 'signup') {
      navigate('/signup');
    }
    setIsSidebarOpen(false);
  };

  // ... (keeping all your style definitions the same)
  const navBarStyles = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px 16px 5px 16px',
    backgroundColor: '#000000',
    position: 'relative',
    zIndex: 10
  };

  const navContainerStyles = {
    background: 'linear-gradient(145deg, #0c0c0c 0%, #1a1a1a 100%)',
    border: '1px solid rgba(202, 217, 227, 0.2)',
    borderRadius: '28px',
    padding: '20px 36px',
    maxWidth: '1200px',
    boxShadow: '0 32px 64px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(202, 217, 227, 0.05)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '52px',
    width: '100%'
  };

  const navLogoStyles = {
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 700,
    fontSize: '26px',
    color: '#cad9e3',
    cursor: 'pointer',
    letterSpacing: '-0.5px',
    transition: 'all 0.2s ease'
  };

  const navMenuStyles = {
    display: (isMobile || isTablet) ? 'none' : 'flex',
    gap: '8px',
    alignItems: 'center',
    background: 'rgba(202, 217, 227, 0.02)',
    borderRadius: '20px',
    padding: '6px'
  };

  const navActionsStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  };

  const navItemStyles = {
    background: 'transparent',
    border: 'none',
    color: '#cad9e3',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    padding: '10px 18px',
    borderRadius: '16px',
    transition: 'all 0.3s ease'
  };

  const deployButtonStyles = {
    background: 'linear-gradient(135deg, #cad9e3 0%, #e8f2f7 100%)',
    color: '#000000',
    border: 'none',
    borderRadius: '20px',
    padding: '13px 28px',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 6px 20px rgba(202, 217, 227, 0.2)'
  };

const addCreditsButtonStyles = {
  background: 'linear-gradient(135deg, #cad9e3 0%, #e8f2f7 100%)',
  color: '#000000',
  border: 'none',
  borderRadius: (isMobile || isTablet) ? '16px' : '20px',
  padding: (isMobile || isTablet) ? '10px 14px' : '13px 28px',
  fontFamily: 'Inter, sans-serif',
  fontSize: (isMobile || isTablet) ? '12px' : '14px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: '0 6px 20px rgba(202, 217, 227, 0.2)',
  whiteSpace: 'nowrap'
};

  const hamburgerButtonStyles = {
    color: '#cad9e3',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    fontSize: '18px',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    marginLeft: '12px'
  };

  const getNavItemHoverStyles = (isHovered, isActive = false) => ({
    ...navItemStyles,
    color: isHovered ? '#ffffff' : '#cad9e3',
    transform: isHovered ? 'translateY(-1px)' : 'none'
  });

  const getNavItemUnderlineStyles = (isHovered) => ({
    position: 'absolute',
    bottom: '-2px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: isHovered ? '80%' : '0%',
    height: '2px',
    background: '#cad9e3',
    transition: 'width 0.3s ease'
  });

  const getDeployButtonHoverStyles = (isHovered) => ({
    ...deployButtonStyles,
    transform: isHovered ? 'translateY(-2px)' : 'none',
    boxShadow: isHovered ? '0 8px 25px rgba(202, 217, 227, 0.3)' : '0 6px 20px rgba(202, 217, 227, 0.2)'
  });

  const getAddCreditsButtonHoverStyles = (isHovered) => ({
    ...addCreditsButtonStyles,
    transform: isHovered ? 'translateY(-2px)' : 'none',
    boxShadow: isHovered ? '0 8px 25px rgba(202, 217, 227, 0.3)' : '0 6px 20px rgba(202, 217, 227, 0.2)'
  });

  const getHamburgerHoverStyles = (isHovered) => ({
    ...hamburgerButtonStyles,
    background: isHovered ? 'rgba(202, 217, 227, 0.1)' : 'none',
    color: isHovered ? '#ffffff' : '#cad9e3',
    transform: isHovered ? 'scale(1.1)' : 'none'
  });

  const getLogoHoverStyles = (isHovered) => ({
    ...navLogoStyles,
    color: isHovered ? '#ffffff' : '#cad9e3',
    textShadow: isHovered ? '0 0 10px rgba(202, 217, 227, 0.3)' : 'none'
  });

  const isAdminRoute = window.location.pathname.includes('/admin');

  return (
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          backgroundColor: '#4B1C1C',
          color: 'white',
          padding: '24px',
          borderRadius: '8px',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>Something went wrong</h2>
          <p style={{ marginBottom: '16px' }}>We've been notified of this error and are working to fix it.</p>
          <button 
            onClick={resetError}
            style={{
              backgroundColor: '#DC2626',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    )} showDialog>
      <div style={{ minHeight: '100vh', backgroundColor: '#000000' }}>
        {/* Navigation Bar */}
        {!isAdminRoute && (
          <nav style={navBarStyles}>
            <div style={navContainerStyles}>
              <div 
                style={getLogoHoverStyles(hoveredElement === 'logo')}
                onClick={() => navigate('/')}
                onMouseEnter={() => setHoveredElement('logo')}
                onMouseLeave={() => setHoveredElement(null)}
              >
                Barrack
              </div>

              <div style={navMenuStyles}>
                <div style={{ position: 'relative' }}>
                  <button 
                    style={getNavItemHoverStyles(hoveredElement === 'docs')}
                    onClick={() => window.open('https://docs.barrack.ai', '_blank')}
                    onMouseEnter={() => setHoveredElement('docs')}
                    onMouseLeave={() => setHoveredElement(null)}
                  >
                    Docs
                  </button>
                  <div style={getNavItemUnderlineStyles(hoveredElement === 'docs')}></div>
                </div>
                
                <div style={{ position: 'relative' }}>
                  <button 
                    style={getNavItemHoverStyles(hoveredElement === 'blog')}
                    onClick={() => window.open('https://blog.barrack.ai', '_blank')}
                    onMouseEnter={() => setHoveredElement('blog')}
                    onMouseLeave={() => setHoveredElement(null)}
                  >
                    Blog
                  </button>
                  <div style={getNavItemUnderlineStyles(hoveredElement === 'blog')}></div>
                </div>
                
                <div style={{ position: 'relative' }}>
                  <button 
                    style={getNavItemHoverStyles(hoveredElement === 'pricing')}
                    onClick={() => navigate('/pricing')}
                    onMouseEnter={() => setHoveredElement('pricing')}
                    onMouseLeave={() => setHoveredElement(null)}
                  >
                    Pricing
                  </button>
                  <div style={getNavItemUnderlineStyles(hoveredElement === 'pricing')}></div>
                </div>
                
                {/* ✅ Wait for initialization before showing auth-dependent nav items */}
                {isInitialized && isAuthenticated ? (
                  <>
                    <div style={{ position: 'relative' }}>
                      <button 
                        style={getNavItemHoverStyles(hoveredElement === 'dashboard', window.location.pathname === '/dashboard')}
                        onClick={() => handleProtectedNavigation('/dashboard')}
                        onMouseEnter={() => setHoveredElement('dashboard')}
                        onMouseLeave={() => setHoveredElement(null)}
                      >
                        Dashboard
                      </button>
                      <div style={getNavItemUnderlineStyles(hoveredElement === 'dashboard')}></div>
                    </div>
                  </>
                ) : isInitialized ? (
                  <>
                    <div style={{ position: 'relative' }}>
                      <button 
                        style={getNavItemHoverStyles(hoveredElement === 'login')}
                        onClick={() => navigate('/login')}
                        onMouseEnter={() => setHoveredElement('login')}
                        onMouseLeave={() => setHoveredElement(null)}
                      >
                        Login
                      </button>
                      <div style={getNavItemUnderlineStyles(hoveredElement === 'login')}></div>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <button 
                        style={getNavItemHoverStyles(hoveredElement === 'signup')}
                        onClick={() => navigate('/signup')}
                        onMouseEnter={() => setHoveredElement('signup')}
                        onMouseLeave={() => setHoveredElement(null)}
                      >
                        Signup
                      </button>
                      <div style={getNavItemUnderlineStyles(hoveredElement === 'signup')}></div>
                    </div>
                  </>
                ) : null}
              </div>

              <div style={navActionsStyles}>
                {window.location.pathname === '/deploy' ? (
                  <button 
                    style={getAddCreditsButtonHoverStyles(hoveredElement === 'addCredits')}
                    onClick={() => {
                      if (isAuthenticated) {
                        setShowCreditsModal(true);
                      } else {
                        localStorage.setItem('post_login_action', 'add_credits');
                        logInfo('Redirecting unauthenticated user to login from add credits', {
                          intendedAction: 'add_credits',
                          currentPath: window.location.pathname
                        });
                        navigate('/login');
                      }
                    }}
                    onMouseEnter={() => setHoveredElement('addCredits')}
                    onMouseLeave={() => setHoveredElement(null)}
                  >
                    Add Credits
                  </button>
                ) : (
                  <button 
                    style={getDeployButtonHoverStyles(hoveredElement === 'deploy')}
                    onClick={() => navigate('/deploy')}
                    onMouseEnter={() => setHoveredElement('deploy')}
                    onMouseLeave={() => setHoveredElement(null)}
                  >
                    Deploy
                  </button>
                )}
                
                {(isMobile || isTablet) && (
                  <button
                    style={getHamburgerHoverStyles(hoveredElement === 'hamburger')}
                    onClick={toggleSidebar}
                    onMouseEnter={() => setHoveredElement('hamburger')}
                    onMouseLeave={() => setHoveredElement(null)}
                    aria-label="Toggle menu"
                  >
                    ☰
                  </button>
                )}
              </div>
            </div>
          </nav>
        )}

        {(isMobile || isTablet) && (
          <Sidebar 
            activePage={window.location.pathname.split('/')[1] || 'home'}
            isMobile={isMobile}
            isTablet={isTablet}
            isSidebarOpen={isSidebarOpen}
            toggleSidebar={toggleSidebar}
            authenticated={isAuthenticated}
            onAuthAction={handleAuthAction}
          />
        )}

        <div>
          <WebSocketProvider>
              <main><Routes>
                {/* Auth Routes */}
                <Route path="/signup" element={<Signup />} />
                <Route path="/login" element={<Login />} />
                <Route path="/callback" element={<Callback />} />
                
                {/* Home Page */}
                <Route path="/" element={<HomePage />} />
                
                <Route path="/s" element={
                  <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
                      <h2 className="text-2xl text-blue-400 font-bold mb-4">GPU Availability</h2>
                      <div className="mb-8">
                        <h3 className="text-lg text-blue-300 font-semibold mb-3">Available GPU Models</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div className="col-span-3 bg-gray-700 rounded-lg p-6 text-center text-gray-300">
                            Loading GPU data...
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                } />
                
                {/* ✅ FIXED: Protected Routes using AuthGuard */}
                <Route path="/billing" element={
                  <AuthGuard>
                    <BillingPage />
                  </AuthGuard>
                } />
                
                <Route path="/deploy" element={
                  <ErrorProvider>
                    <NotificationProvider>
                      <DeployVM />
                    </NotificationProvider>
                  </ErrorProvider>
                } />

                <Route path="/volumes" element={
                  <ErrorProvider>
                    <NotificationProvider>
                      <VolumesPage />
                    </NotificationProvider>
                  </ErrorProvider>
                } />
                
                <Route path="/ssh-keys" element={
                  <ErrorProvider>
                    <NotificationProvider>
                      <SSHKeysPage />
                    </NotificationProvider>
                  </ErrorProvider>
                } />
                
                <Route path="/firewall" element={
                  <ErrorProvider>
                    <NotificationProvider>
                      <FirewallPage />
                    </NotificationProvider>
                  </ErrorProvider>
                } />
                
                <Route path="/snapshots" element={
                  <ErrorProvider>
                    <NotificationProvider>
                      <SnapshotPage />
                    </NotificationProvider>
                  </ErrorProvider>
                } />
                
                <Route path="/images" element={
                  <ErrorProvider>
                    <NotificationProvider>
                      <ImagePage />
                    </NotificationProvider>
                  </ErrorProvider>
                } />

                <Route path="/pricing" element={<PricingPage />} />
                
                <Route path="/account" element={
                  <AuthGuard>
                    <ErrorProvider>
                      <NotificationProvider>
                        <MyAccount />
                      </NotificationProvider>
                    </ErrorProvider>
                  </AuthGuard>
                } />
                
                <Route path="/dashboard" element={
                  <AuthGuard>
                    <ErrorProvider>
                      <NotificationProvider>
                        <Dashboard 
                          userEmail={user?.email}
                          barrackId={user?.barrackId}
                        />
                      </NotificationProvider>
                    </ErrorProvider>
                  </AuthGuard>
                } />
                
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/data-processing-agreement" element={<DataProcessingAgreement />} />

                <Route path="*" element={<Navigate to="/" />} />
              </Routes></main>
          </WebSocketProvider>
        </div>

        <CreditsModal 
          isOpen={showCreditsModal}
          onClose={() => setShowCreditsModal(false)}
          updateBalance={fetchCreditBalance}
        />
      </div>
    </Sentry.ErrorBoundary>
  );
};

export default App;